import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, UsersIcon, SendIcon, ArrowRightIcon, CheckIcon, MailIcon } from 'lucide-react';
const tabs = [{
  id: 'search',
  icon: SearchIcon,
  title: 'Search the local business database',
  description: 'Filter through 200M+ local businesses by location, category, revenue, and tech stack. The ultimate local business owner finder for your sales team.'
}, {
  id: 'contacts',
  icon: UsersIcon,
  title: 'Access business owner emails',
  description: 'Unlike other platforms, we provide verified business owner emails and direct dials. See exactly who you are contacting before you spend credits.'
}, {
  id: 'sequence',
  icon: SendIcon,
  title: 'Built-in email sequencing',
  description: 'Stop exporting CSVs. Launch personalized, multi-step email sequences directly from our local business database to your target prospects.'
}];
export function DatabaseWorkflow() {
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  return <section className="py-24 bg-white" data-id="element-2483">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2484">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2485">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-2486">
            The complete{' '}
            <span className="text-blue-600" data-id="element-2487">local business owner finder</span>{' '}
            workflow
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-2488">
            From discovering local small businesses to booking meetings —
            everything happens in one unified platform.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center" data-id="element-2489">
          {/* Left: Tabs */}
          <div className="lg:col-span-5 space-y-2" data-id="element-2490">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full text-left p-8 rounded-[2rem] transition-all duration-300 ${isActive ? 'bg-slate-50' : 'bg-white hover:bg-slate-50/50'}`} data-id="element-2491">
                  <div className="flex items-start gap-6" data-id="element-2492">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-2493">
                      <tab.icon className="w-6 h-6" data-id="element-2494" />
                    </div>
                    <div data-id="element-2495">
                      <h3 className={`text-xl font-bold mb-3 transition-colors ${isActive ? 'text-slate-900' : 'text-slate-700'}`} data-id="element-2496">
                        {tab.title}
                      </h3>
                      <p className={`text-base leading-relaxed transition-colors ${isActive ? 'text-slate-600' : 'text-slate-500'}`} data-id="element-2497">
                        {tab.description}
                      </p>
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* Right: Dynamic Visuals */}
          <div className="lg:col-span-7" data-id="element-2498">
            <div className="bg-slate-50 rounded-[2.5rem] p-6 md:p-10 min-h-[450px] flex items-center justify-center relative overflow-hidden" data-id="element-2499">
              <AnimatePresence mode="wait" data-id="element-2500">
                {activeTab === 'search' && <motion.div key="search" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md bg-white rounded-3xl p-8 shadow-sm border border-slate-100" data-id="element-2501">
                    <div className="flex items-center justify-between mb-6" data-id="element-2502">
                      <h4 className="font-bold text-slate-900" data-id="element-2503">
                        Local business filters
                      </h4>
                      <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full" data-id="element-2504">
                        12,405 matches
                      </span>
                    </div>
                    <div className="space-y-5" data-id="element-2505">
                      <div data-id="element-2506">
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-2507">
                          Location
                        </label>
                        <div className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm text-slate-700 border border-slate-100" data-id="element-2508">
                          Texas (Statewide)
                        </div>
                      </div>
                      <div data-id="element-2509">
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-2510">
                          Industry
                        </label>
                        <div className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm text-slate-700 border border-slate-100" data-id="element-2511">
                          HVAC & Plumbing
                        </div>
                      </div>
                      <div data-id="element-2512">
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-2513">
                          Employee Count
                        </label>
                        <div className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm text-slate-700 border border-slate-100" data-id="element-2514">
                          10 - 50 employees
                        </div>
                      </div>
                    </div>
                  </motion.div>}

                {activeTab === 'contacts' && <motion.div key="contacts" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-xl" data-id="element-2515">
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden" data-id="element-2516">
                      <div className="bg-slate-50 px-5 py-3 border-b border-slate-100 flex justify-between items-center" data-id="element-2517">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-2518">
                          Business employee email lists
                        </span>
                        <span className="text-xs text-blue-600 font-medium" data-id="element-2519">
                          Viewable before export
                        </span>
                      </div>
                      <div className="divide-y divide-slate-100" data-id="element-2520">
                        {[{
                      name: 'John Smith',
                      role: 'Owner / Founder',
                      email: 'john@smithhvac.com'
                    }, {
                      name: 'Sarah Davis',
                      role: 'Operations Manager',
                      email: 'sarah.d@smithhvac.com'
                    }, {
                      name: 'Mike Wilson',
                      role: 'Lead Technician',
                      email: 'mike@smithhvac.com'
                    }].map((contact, i) => <div key={i} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-2521">
                            <div className="flex items-center gap-3" data-id="element-2522">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs" data-id="element-2523">
                                {contact.name.charAt(0)}
                              </div>
                              <div data-id="element-2524">
                                <div className="text-sm font-bold text-slate-900" data-id="element-2525">
                                  {contact.name}
                                </div>
                                <div className="text-xs text-slate-500" data-id="element-2526">
                                  {contact.role}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-2527">
                              <CheckIcon className="w-4 h-4 text-green-500" data-id="element-2528" />
                              <span className="text-sm text-slate-600" data-id="element-2529">
                                {contact.email}
                              </span>
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </motion.div>}

                {activeTab === 'sequence' && <motion.div key="sequence" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md" data-id="element-2530">
                    <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" data-id="element-2531">
                      <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100" data-id="element-2532">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center" data-id="element-2533">
                          <SendIcon className="w-5 h-5 text-blue-600" data-id="element-2534" />
                        </div>
                        <div data-id="element-2535">
                          <h4 className="font-bold text-slate-900" data-id="element-2536">
                            HVAC Owners Campaign
                          </h4>
                          <p className="text-xs text-slate-500" data-id="element-2537">
                            Active • 450 prospects
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent" data-id="element-2538">
                        <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-2539">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-blue-100 text-blue-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-2540">
                            <MailIcon className="w-4 h-4" data-id="element-2541" />
                          </div>
                          <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-slate-50 p-3 rounded-xl border border-slate-100" data-id="element-2542">
                            <div className="text-xs font-bold text-slate-900 mb-1" data-id="element-2543">
                              Step 1: Initial Email
                            </div>
                            <div className="text-[10px] text-slate-500" data-id="element-2544">
                              Sent immediately
                            </div>
                          </div>
                        </div>

                        <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-2545">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-slate-100 text-slate-400 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-2546">
                            <MailIcon className="w-4 h-4" data-id="element-2547" />
                          </div>
                          <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-3 rounded-xl border border-slate-100" data-id="element-2548">
                            <div className="text-xs font-bold text-slate-900 mb-1" data-id="element-2549">
                              Step 2: Follow up
                            </div>
                            <div className="text-[10px] text-slate-500" data-id="element-2550">
                              Wait 3 days
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}